import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useTheme from '../../hooks/useTheme';

const Layout = ({ children }) => {
    const { user, logout, can } = useAuth();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [openGroups, setOpenGroups] = useState({ catalogo: false, contratos: false, inventario: false, infraestructura: false, monitoreo: false, facturacion: false, gestion: false });

    const isSuperAdmin = user?.role_slug === 'superadmin';
    const isAdmin = user?.role_slug === 'admin';

    const toggleGroup = (group) => {
        setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
    };

    // Items individuales (sin grupo)
    const soloItems = [
        { path: '/dashboard', label: 'Dashboard', icon: '🏠', show: true },
        { path: '/clientes',  label: 'Clientes',  icon: '🏢', show: can('clientes', 'read') },
    ];

    // Grupos colapsables
    const groups = [
        {
            key: 'catalogo',
            label: 'Catálogo',
            icon: '📋',
            show: can('zonas','read') || can('tecnologias','read') || can('planes','read'),
            items: [
                { path: '/zonas',       label: 'Zonas',       icon: '📍', show: can('zonas',       'read') },
                { path: '/tecnologias', label: 'Tecnologías', icon: '📡', show: can('tecnologias', 'read') },
                { path: '/planes',      label: 'Planes',      icon: '📦', show: can('planes',      'read') },
                { path: '/promociones', label: 'Promociones', icon: '🎁', show: can('planes',      'read') },
            ]
        },
        {
            key: 'contratos',
            label: 'Comercial',
            icon: '💼',
            show: true,
            items: [
                { path: '/contratos', label: 'Contratos', icon: '📄', show: true },
            ]
        },
        {
            key: 'marketing',
            label: 'Marketing',
            icon: '🎨',
            show: true,
            items: [
                { path: '/publicidad', label: 'Publicidad', icon: '🖼️', show: true },
            ]
        },
        {
            key: 'inventario',
            label: 'Inventario',
            icon: '📦',
            show: can('inv_marcas','read') || can('inv_modelos','read') || can('inv_equipos','read'),
            items: [
                { path: '/inventario/marcas',  label: 'Marcas',  icon: '🏷️', show: can('inv_marcas',  'read') },
                { path: '/inventario/modelos', label: 'Modelos', icon: '📱', show: can('inv_modelos', 'read') },
                { path: '/inventario/equipos', label: 'Equipos', icon: '🖥️', show: can('inv_equipos', 'read') },
            ]
        },
        {
            key: 'infraestructura',
            label: 'Infraestructura',
            icon: '🌐',
            show: can('inf_nodos','read') || can('inf_olt','read') || can('inf_mikrotik','read') || can('inf_ip_pool','read'),
            items: [
                { path: '/infraestructura/nodos',    label: 'Nodos',     icon: '📍', show: can('inf_nodos',    'read') },
                { path: '/infraestructura/olt',      label: 'OLT',       icon: '📡', show: can('inf_olt',      'read') },
                { path: '/infraestructura/mikrotik', label: 'MikroTik',  icon: '🔧', show: can('inf_mikrotik', 'read') },
                { path: '/infraestructura/ip-pool',  label: 'IP Pool',   icon: '🌐', show: can('inf_ip_pool',  'read') },
            ]
        },
        {
            key: 'monitoreo',
            label: 'Monitoreo',
            icon: '📊',
            show: can('inf_olt', 'read'),
            items: [
                { path: '/monitoreo/olt', label: 'OLT Dashboard', icon: '📡', show: can('inf_olt', 'read') },
            ]
        },
        {
            key: 'facturacion',
            label: 'Facturación',
            icon: '💰',
            show: true,
            items: [
                { path: '/facturacion/facturas', label: 'Facturas', icon: '🧾', show: true },
                { path: '/facturacion/pagos',    label: 'Pagos',    icon: '💳', show: true },
            ]
        },
        {
            key: 'gestion',
            label: 'Gestión',
            icon: '⚙️',
            show: can('usuarios','read') || can('roles','read') || isSuperAdmin || isAdmin,
            items: [
                { path: '/users',    label: 'Usuarios',      icon: '👥', show: can('usuarios', 'read') },
                { path: '/roles',    label: 'Roles',         icon: '🔐', show: can('roles',    'read') },
                { path: '/settings', label: 'Configuración', icon: '⚙️', show: isSuperAdmin || isAdmin },
            ]
        },
    ];

    // Para el título del header — buscar en todos los ítems
    const allItems = [
        ...soloItems,
        ...groups.flatMap(g => g.items)
    ];

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const isActive = (path) => location.pathname === path;
    const isGroupActive = (group) => group.items.some(i => location.pathname === i.path);

    const linkClass = (path) =>
        `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
            isActive(path) ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
        }`;

    return (
        <div className="flex h-screen overflow-hidden" style={{ backgroundColor: 'var(--color-fondo)' }}>

            {/* Sidebar */}
            <aside
                className={`${sidebarOpen ? 'w-64' : 'w-16'} transition-all duration-300 flex flex-col text-white flex-shrink-0`}
                style={{ backgroundColor: 'var(--color-primary)' }}
            >
                <div className="flex items-center justify-between px-4 py-4 border-b border-white/20">
                    {sidebarOpen && (
                        <div className="flex items-center gap-2">
                            {theme.empresa_logo ? (
                                <img src={theme.empresa_logo} alt="Logo" className="h-8 object-contain" />
                            ) : (
                                <span className="font-bold text-lg">{theme.empresa_nombre}</span>
                            )}
                        </div>
                    )}
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white/80 hover:text-white text-xl">
                        {sidebarOpen ? '◀' : '▶'}
                    </button>
                </div>

                <nav className="flex-1 py-2 overflow-y-auto">
                    {/* Items individuales */}
                    {soloItems.filter(i => i.show).map(item => (
                        <Link key={item.path} to={item.path} className={linkClass(item.path)}>
                            <span className="text-lg flex-shrink-0">{item.icon}</span>
                            {sidebarOpen && <span>{item.label}</span>}
                        </Link>
                    ))}

                    {/* Grupos colapsables */}
                    {groups.filter(g => g.show).map(group => (
                        <div key={group.key}>
                            {/* Cabecera del grupo */}
                            <button
                                onClick={() => sidebarOpen && toggleGroup(group.key)}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors
                                    ${isGroupActive(group) ? 'bg-white/10' : 'hover:bg-white/10'}
                                    ${sidebarOpen ? 'justify-between' : 'justify-center'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-lg flex-shrink-0">{group.icon}</span>
                                    {sidebarOpen && <span className="font-medium text-white/90">{group.label}</span>}
                                </div>
                                {sidebarOpen && (
                                    <span className="text-white/60 text-xs">
                                        {openGroups[group.key] ? '▾' : '▸'}
                                    </span>
                                )}
                            </button>

                            {/* Items del grupo */}
                            {(openGroups[group.key] || !sidebarOpen) && (
                                <div className={sidebarOpen ? 'bg-black/10' : ''}>
                                    {group.items.filter(i => i.show).map(item => (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            className={`flex items-center gap-3 py-2.5 text-sm transition-colors
                                                ${sidebarOpen ? 'pl-10 pr-4' : 'px-4 justify-center'}
                                                ${isActive(item.path) ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'}`}
                                        >
                                            <span className="text-base flex-shrink-0">{item.icon}</span>
                                            {sidebarOpen && <span className="text-white/90">{item.label}</span>}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                <div className="border-t border-white/20 p-4">
                    <div className="flex items-center gap-3">
                        {user?.foto_url ? (
                            <img src={user.foto_url} alt="Foto" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                {user?.nombre?.charAt(0)}
                            </div>
                        )}
                        {sidebarOpen && (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{user?.nombre} {user?.apellido}</p>
                                <p className="text-xs text-white/60 truncate">{user?.role}</p>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <header className="bg-white shadow-sm px-6 py-3 flex items-center justify-between flex-shrink-0">
                    <h2 className="text-gray-700 font-semibold">
                        {allItems.find(i => i.path === location.pathname)?.label || 'CRM'}
                    </h2>
                    <div className="relative">
                        <button
                            onClick={() => setUserMenuOpen(!userMenuOpen)}
                            className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
                        >
                            {user?.foto_url ? (
                                <img src={user.foto_url} alt="Foto" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                                <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                                    style={{ backgroundColor: 'var(--color-primary)' }}
                                >
                                    {user?.nombre?.charAt(0)}
                                </div>
                            )}
                            <span>{user?.nombre}</span>
                            <span className="text-gray-400">▾</span>
                        </button>
                        {userMenuOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-50">
                                <Link
                                    to="/profile"
                                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                                    onClick={() => setUserMenuOpen(false)}
                                >
                                    Mi perfil
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                                >
                                    Cerrar sesión
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <main className="flex-1 overflow-auto p-6">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;
